<script lang="ts">
	import { createEventDispatcher } from 'svelte'
	import { Icon, type IconSource } from 'svelte-hero-icons'

	import LoadingIcon from '$lib/components/Common/Icons/LoadingIcon.svelte'
    import { UserActionEnabled } from '$lib/stores/user/manager'
	import { user_store } from '$lib/stores/user/user'

	import { BORDER_GROUP_TYPE, CLASS_SIZE } from '../ComponentProperties'
	import { BUTTON_TYPE } from './Button_types'

	
	const dispatch = createEventDispatcher()

	export let data_test_id = ''
	export let id = ''

	export let type: BUTTON_TYPE = BUTTON_TYPE.SECONDARY
	export let group_type: BORDER_GROUP_TYPE = BORDER_GROUP_TYPE.ALONE

	export { className as class }
	export let classSize: CLASS_SIZE = CLASS_SIZE.MD

	export let loading = false
	export let centered = false
	// export let compact = false
	export let square = false
	export let selected = false
	
	export let html_type: 'button' | 'reset' | 'submit' = 'button'
	export let preload_data: 'none' | 'off' | 'hover' | 'tap' = 'none'
	export let href: string | undefined = undefined
	export let useClick = true
	export let data_tooltip = ''
	export let disabled = false

	export let iconSrc: IconSource | undefined = undefined
	export let permission = ''

	let className = ''
	
	$: dataSveltekitPreloadData = preload_data == 'none' ? null : preload_data
	$: iconClass = classSize == CLASS_SIZE.SM ? 'iconClassCompact' : 'iconClassDefault'
	$: loadingSize = classSize == CLASS_SIZE.SM ? 4 : 5
	
	
	$: internal_disabled = disabled
	$: if (!disabled && permission && $user_store) {
		UserActionEnabled(permission).then(result => {
			internal_disabled = !result
		})
	}

	function handleClick(event: CustomEvent) {
		if (!useClick) return

		if (html_type != 'submit'){
			event.preventDefault()
			event.stopPropagation()
		}

		if (internal_disabled) return

		dispatch('click', event)
	}
</script>
 
<svelte:element 
	{id}
	this={href ? 'a' : 'button'}
	type={html_type}
	{href}
	data-test-id={data_test_id}
	class:centered
	class:disabled={internal_disabled}
	class:selected
	class:square
	class="default {type} {className} {classSize} {group_type} "
	data-sveltekit-preload-data={dataSveltekitPreloadData}
	data-tooltip={data_tooltip}
	{...$$restProps}
	on:click={ (e) => handleClick(e)}
>
	{#if loading}
		<LoadingIcon size={loadingSize} loading={true} />
	{:else if iconSrc}
		<div class={iconClass}>
			<Icon src={iconSrc}  />
		</div>
	{/if}
	<slot />

</svelte:element>

<style lang="postcss">
	.default {
		/* include icons and text on the same line without needing a container */
		/* @apply grid grid-flow-col gap-2 items-center place-items-center whitespace-nowrap; */
		@apply text-gray-700;
		@apply flex items-center justify-center gap-2;
		/* @apply h-10 px-4; */
		@apply border border-solid border-neutral-300 outline-none;
		width: inherit;
		border-radius: 4px;
		font-style: normal;
		font-weight: 400;
	}

	/* .default:focus, */
	/* .default.selected { */
	/* @apply ring-offset-2 ring-2 mix-blend-multiply; */
	/* @apply ring-offset-2 ring-2; */
	/* } */
	.centered {
		@apply justify-self-center;
	}
	.disabled {
		@apply cursor-not-allowed;
	}

	/* ------ */
	/* size   */
	.size_xs {
		@apply text-sm font-medium;
		padding: 4px 8px;
	}
	.size_sm {
		@apply text-sm font-medium;
		/* DocMainActions for Estimate: Generate Invoice (uses 2 rows) */
		line-height: 16px !important;
		padding: 6px 14px;
		height: 34px;
	}
	.size_md {
		/* @apply text-base; */
		@apply text-sm font-medium;
		padding: 8px 16px;
		height: 40px;
	}
	.size_lg {
		@apply text-base font-medium;
		padding: 8px 16px;
	}

	.square {
		/* @apply p-0; */
		/* padding: 0px !important; */
		padding: 0px;
		aspect-ratio: 1 / 1;
	}

	/* ------------------------------------------ */
	/* primary button */
	.primary {
		@apply text-white bg-emerald-500 border-emerald-500;
	}
	.primary:hover:not(.disabled) {
		@apply bg-emerald-600 border-emerald-600;
	}
	.primary.disabled {
		/* @apply bg-emerald-800 border-emerald-800; */
		@apply bg-gray-300 border-gray-300;
	}
	.primary:focus {
		@apply ring-emerald-500;
	}

	/* ------------------------------------------ */
	/* secondary button */
	.secondary {
		@apply bg-gray-50 border-gray-200;
	}
	.secondary:hover:not(.disabled) {
		@apply bg-gray-100 border-gray-400;
		@apply text-blue-500;
	}
	.secondary.disabled {
		@apply bg-gray-200 border-gray-300;
	}
	.secondary:focus {
		@apply ring-gray-500;
	}
	.secondary.selected {
		@apply bg-gray-100;
	}

	/* ------------------------------------------ */
	/* danger button */
	.danger {
		@apply text-white bg-red-300 border-red-100;
	}
	.danger:hover:not(.disabled) {
		@apply bg-red-200 border-red-300;
	}
	.danger.disabled {
		@apply bg-red-300;
	}
	.danger:focus {
		@apply ring-red-500;
	}

	/* ------------------------------------------ */
	/* menu button */
	.menu {
		@apply border border-solid border-neutral-300 rounded-lg outline-none;
		@apply bg-white;
	}
	.menu:hover:not(.disabled) {
		@apply bg-gray-100;
	}
	.menu:focus {
		@apply ring-gray-500;
	}

	/* ------------------------------------------ */
	/* buttons group */
	/* .group_left {
		@apply rounded-r-none;
	}
	.group_center {
		@apply rounded-none;
	}
	.group_right {
		@apply rounded-l-none;
	} */

	/* ------------------------------------------ */
	/* icon class */
	.iconClassDefault {
		@apply w-5 h-5;
	}
	.iconClassCompact {
		@apply w-4 h-4;
	}
</style>
